import React from 'react';

const ModernHeader = ({
  currentProject,
  currentFile,
  onSave,
  onPreview,
  onSettings,
  backendHealth,
  isSaving,
  onToggleSidebar,
  onToggleRightPanel
}) => {
  const getHealthColor = () => {
    switch (backendHealth) {
      case 'healthy': return 'bg-green-500';
      case 'degraded': return 'bg-yellow-500';
      case 'unhealthy': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <header className="h-14 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-gray-700 flex items-center justify-between px-2 sm:px-4 shadow-lg">
      {/* Left: Mobile Menu + Logo + Project Info */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
        {/* Mobile Menu Button */}
        <button
          onClick={onToggleSidebar}
          className="md:hidden p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors flex-shrink-0"
          title="Toggle Menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">CW</span>
          </div>
          <span className="text-white font-semibold text-base sm:text-lg hidden sm:block">Code Wizard</span>
        </div>

        {/* Project Info - Truncated on mobile */}
        <div className="flex items-center gap-2 min-w-0 hidden lg:flex">
          {currentProject && (
            <>
              <div className="h-6 w-px bg-gray-600"></div>
              <div className="flex items-center gap-2 min-w-0">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span className="text-gray-300 text-sm truncate">{currentProject.name}</span>
              </div>
            </>
          )}

          {currentFile && (
            <>
              <span className="text-gray-600">/</span>
              <span className="text-blue-400 text-sm font-mono truncate">{currentFile.name}</span>
              {currentFile.modified_locally && (
                <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Center: Quick Actions - Hidden on small mobile, icons only on tablet */}
      <div className="hidden sm:flex items-center gap-1 sm:gap-2">
        <button
          onClick={onSave}
          disabled={isSaving || !currentFile}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-md text-sm font-medium transition-colors"
          title="Save (Ctrl+S)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
          </svg>
          <span className="hidden md:inline">{isSaving ? 'Saving...' : 'Save'}</span>
        </button>

        <button
          onClick={onPreview}
          disabled={!currentFile}
          className="hidden md:flex items-center gap-2 px-4 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-md text-sm font-medium transition-colors"
          title="Preview"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span>Preview</span>
        </button>
      </div>

      {/* Right: Status & Settings */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Backend Status - Compact on mobile */}
        <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 bg-gray-800 rounded-full">
          <div className={`w-2 h-2 rounded-full ${getHealthColor()} animate-pulse`}></div>
          <span className="text-xs text-gray-400 capitalize hidden sm:inline">{backendHealth || 'checking'}</span>
        </div>

        {/* Settings - Hidden on mobile */}
        <button
          onClick={onSettings}
          className="hidden sm:block p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
          title="Settings"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>
    </header>
  );
};

export default ModernHeader;
